import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClientGrid = ({ clients, onClientSelect, selectedClientId, onBulkAction, onNewClient }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      completed: 'bg-primary/10 text-primary border-primary/20',
      cancelled: 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || colors?.active;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: 'text-success',
      partial: 'text-warning',
      overdue: 'text-error',
      pending: 'text-muted-foreground'
    };
    return colors?.[status] || colors?.pending;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients?.map(c => c?.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev?.includes(clientId) 
        ? prev?.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('pt-BR');
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="p-4 border-b border-border bg-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Clientes ({clients?.length})
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Exportar
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="UserPlus"
              iconPosition="left"
              onClick={onNewClient}
            >
              Novo Cliente
            </Button>
          </div>
        </div>

        {selectedClients?.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-md">
            <span className="text-sm text-foreground">
              {selectedClients?.length} cliente(s) selecionado(s)
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                size="xs"
                iconName="Mail"
                onClick={() => onBulkAction('email', selectedClients)}
              >
                Enviar Email
              </Button>
              <Button
                variant="outline"
                size="xs"
                iconName="Bell"
                onClick={() => onBulkAction('reminder', selectedClients)}
              >
                Lembrete
              </Button>
              <Button
                variant="outline"
                size="xs"
                iconName="X"
                onClick={() => setSelectedClients([])}
              >
                Limpar
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border"
                />
              </th>
              <th className="p-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Cliente
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="p-3 text-left">
                <button
                  onClick={() => handleSort('transactionName')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Nome da Transação
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="p-3 text-left">
                <button
                  onClick={() => handleSort('transactionAmount')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Valor da Transação
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="p-3 text-left">
                <button
                  onClick={() => handleSort('totalValue')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Valor Total
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="p-3 text-left">
                <span className="text-sm font-medium text-foreground">Status</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {clients?.map((client) => (
              <tr
                key={client?.id}
                onClick={() => onClientSelect(client?.id)}
                className={`border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${
                  selectedClientId === client?.id ? 'bg-primary/5' : ''
                }`}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedClients?.includes(client?.id)}
                    onChange={(e) => {
                      e?.stopPropagation();
                      handleSelectClient(client?.id);
                    }}
                    onClick={(e) => e?.stopPropagation()}
                    className="w-4 h-4 rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <Image
                      src={client?.avatar}
                      alt={client?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground">{client?.name}</div>
                      <div className="text-xs text-muted-foreground">{client?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{client?.transactionName || '-'}</span>
                  </div>
                </td>
                <td className="p-3">
                  <span className="text-sm font-medium text-foreground">
                    {client?.transactionAmount ? formatCurrency(client?.transactionAmount) : '-'}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-sm font-medium text-foreground">
                    {formatCurrency(client?.totalValue)}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(client?.status)}`}>
                    {client?.statusLabel}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientGrid;